import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, TrendingUp, RefreshCw, Clock, Lock, Unlock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMarketIndices } from "@/hooks/market-data/useMarketIndices";
import { isMarketOpen as checkMarketStatus } from "@/services/alpaca-market-indices";

interface ExpandedMarketOverviewProps {
  onSymbolSelect?: (symbol: string) => void;
}

/**
 * Format a timestamp in a user-friendly way
 * Shows time, and "just now" if within the last minute
 */
const formatUpdateTime = (timestamp: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
};

/**
 * Format time remaining until next market event
 */
const formatTimeRemaining = (targetTime: Date): string => {
  const now = new Date();
  const diff = targetTime.getTime() - now.getTime();
  
  if (diff <= 0) return 'Now';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

/**
 * Get current Eastern Time formatted string
 */
const getCurrentEasternTime = (): string => {
  const now = new Date();
  // Convert to Eastern Time
  const easternTime = now.toLocaleString("en-US", {
    timeZone: "America/New_York",
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
  return easternTime;
};

/**
 * ExpandedMarketOverview component
 * Displays key market indices with performance metrics
 * Dynamically fetches data using Alpaca's real-time data API
 */
export const ExpandedMarketOverview: React.FC<ExpandedMarketOverviewProps> = ({
  onSymbolSelect
}) => {
  const { 
    indices: marketIndices, 
    loading: isLoading, 
    error, 
    lastUpdated,
    refreshData 
  } = useMarketIndices({ refreshInterval: 5 * 60 * 1000 }); // 5 minutes refresh interval
  
  const [marketStatus, setMarketStatus] = useState<{
    isOpen: boolean;
    nextOpenTime: Date | null;
    nextCloseTime: Date | null;
  } | null>(null);
  const [currentEasternTime, setCurrentEasternTime] = useState(getCurrentEasternTime());
  
  // Fetch market status and update current time
  useEffect(() => {
         const fetchMarketStatus = async () => {
       try {
         const status = await checkMarketStatus();
         setMarketStatus(status);
       } catch (error) {
         console.error('Error fetching market status:', error);
       }
     };
    
    fetchMarketStatus();
    
    // Update current time every second
    const timeInterval = setInterval(() => {
      setCurrentEasternTime(getCurrentEasternTime());
    }, 1000);
    
    // Refresh market status every minute
    const statusInterval = setInterval(fetchMarketStatus, 60000);
    
    return () => {
      clearInterval(timeInterval);
      clearInterval(statusInterval);
    };
  }, []);
  
  // Determine if market is currently open (fallback to marketIndices if marketStatus not available)
  const isMarketOpen = marketStatus?.isOpen ?? (marketIndices.length > 0 && marketIndices[0].marketStatus === 'open');
  
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg font-semibold">Market Overview</CardTitle>
          <CardDescription className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              Key market indices
              <span className="mx-1">•</span>
              <span className="flex items-center">
                {isMarketOpen ? (
                  <>
                    <Unlock className="h-3 w-3 text-green-500 mr-1" />
                    <span className="text-green-600 font-medium">Market Open</span>
                  </>
                ) : (
                  <>
                    <Lock className="h-3 w-3 text-amber-500 mr-1" />
                    <span className="text-amber-600 font-medium">Market Closed</span>
                  </>
                )}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>ET: {currentEasternTime}</span>
              {marketStatus && (
                <>
                  <span className="mx-1">•</span>
                  {isMarketOpen && marketStatus.nextCloseTime ? (
                    <span>Closes in {formatTimeRemaining(marketStatus.nextCloseTime)}</span>
                  ) : (!isMarketOpen && marketStatus.nextOpenTime) ? (
                    <span>Opens in {formatTimeRemaining(marketStatus.nextOpenTime)}</span>
                  ) : null}
                </>
              )}
            </div>
          </CardDescription>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={refreshData} 
                disabled={isLoading}
                className="mr-1"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Refresh market data</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading && marketIndices.length === 0 ? (
          <div className="p-4 space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" data-testid="loading-skeleton" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">
            Error: {error}
          </div>
        ) : marketIndices.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No market data available
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {marketIndices.map((item, i) => (
              <TooltipProvider key={i}>
                <Tooltip delayDuration={50}>
                  <TooltipTrigger asChild>
                    <div 
                      className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onSymbolSelect ? onSymbolSelect(item.symbol) : {}}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-sm font-medium">{item.name}</div>
                        <Badge className="bg-secondary border-secondary text-secondary-foreground hover:bg-secondary/80">
                          {item.symbol}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">${item.value.toFixed(2)}</div>
                        <div className="flex items-center gap-1 text-xs">
                          <TrendingUp className={`h-3 w-3 ${item.changePercent >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                          <Badge
                            variant={item.changePercent >= 0 ? "default" : "destructive"}
                            className={`${item.changePercent >= 0 ? 'bg-green-500' : ''}`}
                          >
                            {item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(2)}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-sm">
                    View detailed information about {item.name} ({item.symbol})
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            
            {lastUpdated && (
              <div className="px-4 py-2 text-xs text-gray-500 flex items-center justify-end">
                <Clock className="h-3 w-3 mr-1" />
                <span>
                  Last updated: {formatUpdateTime(lastUpdated)}
                  {isLoading && <span className="ml-2">(refreshing...)</span>}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};